import React, { useEffect } from 'react';
import type { SetOptional } from 'type-fest';
import { useQuery } from '@apollo/client';
import ExternalBrandLogo from '../../components/ExternalBrandLogo';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import { MentionStatus } from '../../graphql/generated/graphql';
import getExternalBrandLogoProps from '../../integrations/getExternalBrandLogoProps';
import useIsPublicView from '../../utils/useIsPublicView';
import type { MentionComponentProps } from '../formats/Mention';
import getEditorMentionUrl from '../utils/getEditorMentionUrl';
import EDITOR_INTEGRATION_MENTIONS from './EditorIntegrationMentions';
import MentionBase from './MentionBase';

const CLOSED_STATUSES = [
  MentionStatus.Closed,
  MentionStatus.Merged,
  MentionStatus.Deleted,
  MentionStatus.Archived,
];

const IS_MENTION_CLOSED_FRAGMENT = gql(`
  fragment IsMentionClosed_mention on Mention {
    id
    status
  }
`);

const isMentionClosed = (
  mention?: DocumentType<typeof IS_MENTION_CLOSED_FRAGMENT>,
) => {
  return mention?.status ? CLOSED_STATUSES.includes(mention.status) : false;
};

const MENTION_QUERY = gql(/** @complexity 4 */ `
  query mention($type: MentionType!, $id: ID!) {
    mention(type: $type, id: $id) {
      id
      title
      ...IsMentionClosed_mention
    }
  }
`);

type IntegrationMentionProps = SetOptional<
  MentionComponentProps,
  'updateCache' | 'blot'
>;

const IntegrationMention = ({
  id,
  type,
  params,
  content,
  mentionText,
  updateCache,
  link,
  selected,
}: IntegrationMentionProps) => {
  const isPublicView = useIsPublicView();
  const apiEnum = EDITOR_INTEGRATION_MENTIONS[type]?.apiEnum;
  const skip = isPublicView || !apiEnum;

  const { data: { mention } = {} } = useQuery(MENTION_QUERY, {
    skip,
    variables: { id, type: apiEnum! },
  });

  const to = getEditorMentionUrl({ type, id, params });
  const text = mentionText || mention?.title || content || to;

  useEffect(() => {
    if (mention) {
      updateCache?.(mention.title);
    }
  }, [updateCache, mention]);

  return (
    <MentionBase
      type="external"
      to={link ? to : undefined}
      closed={isMentionClosed(mention)}
      icon={<ExternalBrandLogo inline {...getExternalBrandLogoProps(type)} />}
      content={text}
      newTab
      selected={selected}
    />
  );
};

export default IntegrationMention;
